import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import { useState } from "react";
import Auth from "../../Auth";
import { SudoTooltip } from "../../sudokit/components/SudoTooltip";

type TrialStatusToggleProps = {
  userId?: string;
  subscription?: {
    activeTrial?: () => boolean;
    usesQuota?: boolean;
  } | null;
  toolsEnabled: boolean;
  setToast: (content: string) => void;
  onUpdated: () => void;
};

export function TrialStatusToggle({
  userId,
  subscription,
  toolsEnabled,
  setToast,
  onUpdated,
}: TrialStatusToggleProps) {
  const confirmAction = useConfirmation(userId);
  const [loading, setLoading] = useState(false);
  const isOnTrial = subscription?.activeTrial?.() ?? false;

  if (!subscription || !userId) {
    return null;
  }

  const handleToggle = async () => {
    const nextEnabled = !isOnTrial;
    const confirmMessage = nextEnabled ? "Put this user into trial status for testing? This clears their plan in Firestore and may desync from Stripe." : subscription.usesQuota ? "Mark this user as a paid member for testing? This assigns a default plan in Firestore and may desync from Stripe." : "End this user's trial period for testing? This updates trialPeriodEnd in Firestore and may desync from Stripe.";
    await confirmAction({
      title: "Change trial status",
      message: confirmMessage,
      actionLabel: "Change status",
      destructive: false
    }, async () => {
      setLoading(true);
      try {
        const result = await Auth.fetchWithIdToken(`/api/admin/user/${userId}/trial-status`, {
          enabled: nextEnabled
        });
        setToast(result?.activeTrial ? "User is now in trial for testing" : "User is no longer in trial for testing");
        onUpdated();
      } catch (error: any) {
        setToast(error?.message || "Failed to update trial status");
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
      <h4 className="text-base font-semibold text-amber-900 mb-2">Trial Status (Testing)</h4>
      <p className="text-sm text-amber-800 mb-3">
        Toggle whether this account reads as <code className="text-xs">activeTrial()</code> in the app.
        For credit-based plans, trial means no <code className="text-xs">stripePlanId</code>; member assigns a default test plan.
        Changes are Firestore-only and can desync Stripe.
      </p>
      <label className="flex items-center gap-3 text-sm text-amber-900">
        <SudoTooltip text="Flip trial membership for manual QA. User may need to refresh the app to see billing UI changes.">
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer"
            checked={isOnTrial}
            disabled={!toolsEnabled || loading}
            onChange={() => {
              void handleToggle();
            }}
          />
        </SudoTooltip>
        <span className="font-medium">Is in trial</span>
        <span className="text-amber-700">({isOnTrial ? "Yes" : "No"})</span>
      </label>
    </div>
  );
}
